import React from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { GroupBasePath } from '../../pages/GroupOwner';
import { useBusiness, useIsManagementUser } from '../../context/hooks';
import { HugeiconsIcon } from '@hugeicons/react';
import { HomeIcon } from '@hugeicons-pro/core-bulk-rounded';
import css from './Breadcrumb.module.css';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

const Separator: React.FC = () => <span className={css.breadcrumb__separator}>/</span>;

const Breadcrumb: React.FC = () => {
  const location = useLocation();
  const params = useParams<any>();
  const { groupId, groupType } = params;
  const group = useBusiness(groupId);
  const isManagementUser = useIsManagementUser();

  if (isManagementUser) return null;

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const pathname = location.pathname;
    const subscriptionName = group?.name || 'Subscription Details';

    if (pathname === GroupBasePath || pathname === `${GroupBasePath}/` || pathname === `${GroupBasePath}/dashboard`) {
      return [{ label: 'Account', path: `${GroupBasePath}/` }, { label: 'Dashboard' }];
    }
    if (pathname === `${GroupBasePath}/packages`) {
      return [{ label: 'Account', path: `${GroupBasePath}/` }, { label: 'Portfolio' }];
    }
    if (pathname === `${GroupBasePath}/billing`) {
      return [{ label: 'Account', path: `${GroupBasePath}/` }, { label: 'Billing' }];
    }
    if (pathname.startsWith(`${GroupBasePath}/billing/invoice/`)) {
      return [
        { label: 'Account', path: `${GroupBasePath}/` },
        { label: 'Billing', path: `${GroupBasePath}/billing` },
        { label: 'Invoice' },
      ];
    }
    if (pathname === `${GroupBasePath}/payments`) {
      return [{ label: 'Account', path: `${GroupBasePath}/` }, { label: 'Payment' }];
    }

    if (groupId && groupType && pathname === `${GroupBasePath}/packages/${groupType}/${groupId}/bulletin-board`) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Communication Center' },
      ];
    }
    if (groupId && groupType && pathname === `${GroupBasePath}/packages/${groupType}/${groupId}/emergency-center`) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Emergency Center' },
      ];
    }
    if (groupId && groupType && pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/call-logs`)) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Call Audit Log' },
      ];
    }
    if (
      groupId &&
      groupType &&
      (pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/bulletin-board/new`) ||
        pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/bulletin-board/`) ||
        pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/emergency-center/new`) ||
        pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/new-bulletin-board`) ||
        pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/edit-bulletin-board`) ||
        pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/view-bulletin-board`))
    ) {
      const isNewMode =
        pathname.includes('/bulletin-board/new') ||
        pathname.includes('/emergency-center/new') ||
        pathname.includes('new-bulletin-board') ||
        (pathname.includes('edit-bulletin-board') && !pathname.match(/edit-bulletin-board\/([^/]+)$/));
      const isEditMode =
        (pathname.includes('/bulletin-board/') && pathname.includes('/edit')) || (pathname.includes('edit-bulletin-board') && pathname.match(/edit-bulletin-board\/([^/]+)$/));
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        {
          label: pathname.includes('/emergency-center') ? 'Emergency Center' : 'Communication Center',
          path: `${GroupBasePath}/packages/${groupType}/${groupId}/${pathname.includes('/emergency-center') ? 'emergency-center' : 'bulletin-board'}`,
        },
        { label: isNewMode ? 'New' : isEditMode ? 'Edit' : 'View Post' },
      ];
    }
    if (groupId && groupType && pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/service-tags/new`)) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Services', path: `${GroupBasePath}/packages/${groupType}/${groupId}/service-tags` },
        { label: 'New' },
      ];
    }
    if (groupId && groupType && pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/service-tags/`) && pathname.includes('/edit')) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Services', path: `${GroupBasePath}/packages/${groupType}/${groupId}/service-tags` },
        { label: 'Edit' },
      ];
    }
    if (groupId && groupType && pathname === `${GroupBasePath}/packages/${groupType}/${groupId}/service-tags`) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Services' },
      ];
    }
    if (groupId && groupType && pathname.includes(`${GroupBasePath}/packages/${groupType}/${groupId}/extensions`)) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Extensions' },
      ];
    }
    if (groupId && groupType && pathname === `${GroupBasePath}/packages/${groupType}/${groupId}/design`) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Business Branding' },
      ];
    }
    if (groupId && groupType && pathname === `${GroupBasePath}/packages/${groupType}/${groupId}/customize-pingpad`) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Communication Policies' },
      ];
    }
    if (groupId && groupType && pathname === `${GroupBasePath}/packages/${groupType}/${groupId}/service-plan`) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Service Plans' },
      ];
    }
    if (groupId && groupType && pathname.startsWith(`${GroupBasePath}/packages/${groupType}/${groupId}/`)) {
      const subPage = pathname.split('/').pop();
      const labelMap: Record<string, string> = {
        members: 'Manage Members',
        info: 'Information',
        pings: 'Manage Pings',
        payment: 'Payment',
        activate: 'Activate Subscription',
        renew: 'Activate Subscription',
        dashboard: 'Dashboard',
        roles: 'Roles',
        'door-management': 'Door Management',
      };
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: labelMap[subPage ?? ''] || 'Details' },
      ];
    }
    if (groupId && groupType && pathname === `${GroupBasePath}/packages/${groupType}/${groupId}`) {
      return [
        { label: 'Account', path: `${GroupBasePath}/packages` },
        { label: 'Portfolio', path: `${GroupBasePath}/packages` },
        { label: subscriptionName, path: `${GroupBasePath}/packages/${groupType}/${groupId}` },
        { label: 'Business Information' },
      ];
    }

    return [{ label: 'Account', path: `${GroupBasePath}/` }, { label: 'Profile' }];
  };

  const items = getBreadcrumbItems();

  return (
    <nav className={css.breadcrumb} aria-label="breadcrumb">
      {/* Home icon links to the root */}
      <Link to={`${GroupBasePath}/`} className={css.breadcrumb__homeLink} aria-label="Home">
        <HugeiconsIcon icon={HomeIcon} size={16} color="#94a3b8" />
      </Link>

      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <React.Fragment key={index}>
            <Separator />
            {item.path && !isLast ? (
              <Link to={item.path} className={css.breadcrumb__link}>
                {item.label}
              </Link>
            ) : (
              <span className={isLast ? css.breadcrumb__current : css.breadcrumb__link}>{item.label}</span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
